import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-shopify-utils/local-storage-tools";
import { makePostRequest } from "@redotech/redo-shopify-utils/request-helper";
import { AsyncStorage } from "../events-provider/storefront-events-provider";
import {
  CheckoutCompletedEvent,
  StorefrontTrackingEvent,
  StorefrontTrackingEventCallbackGeneric,
  StorefrontTrackingEventCallbackList,
  StorefrontTrackingEventType,
} from "../storefront-tracking-event";

export const splitBasicEvents = [
  "page_viewed",
  "product_added_to_cart",
  "cart_viewed",
  "checkout_started",
];

export function getCoverageSplitEventsCallbacks({
  storage,
  baseRequestUrl,
  widgetId,
}: {
  storage: AsyncStorage;
  baseRequestUrl: string;
  widgetId: string;
}): StorefrontTrackingEventCallbackList {
  /* START LOGIC FOR (COVERAGE) SPLITS */
  async function getCoverageTreatmentID() {
    let treatment_id = await getLocalStorageWithExpiry(
      "redo_treatment_id",
      storage,
    );
    while (!treatment_id) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      treatment_id = await getLocalStorageWithExpiry(
        "redo_treatment_id",
        storage,
      );
    }
    return treatment_id;
  }

  async function splitBasicEventCallback(
    event: StorefrontTrackingEvent,
  ): Promise<void> {
    if (await getLocalStorageWithExpiry(`redo_${event.type}`, storage)) {
      return;
    }
    await setLocalStorageWithExpiry(`redo_${event.type}`, true, 2, storage);
    const payload = {
      event_name: event.type,
      treatment_id: await getCoverageTreatmentID(),
    };

    await makePostRequest(
      `${baseRequestUrl}/${widgetId}/treatment-data/basic-event`,
      payload,
    );
  }

  async function splitCheckoutCompletedCallback(
    event: CheckoutCompletedEvent,
  ): Promise<void> {
    const checkout = event.data.checkout;
    const line_items = checkout.lineItems;
    let redo_product_id = "";
    let num_merchant_items_purchased = 0;

    for (const item of line_items) {
      if (item.variant?.product.vendor === "re:do") {
        redo_product_id = item.variant?.product.id;
      } else {
        num_merchant_items_purchased += item.quantity;
      }
    }
    const payload = {
      event_name: event.type,
      treatment_id: await getCoverageTreatmentID(),
      checkout_details: {
        total_price: checkout.totalPrice.amount,
        subtotal_price: checkout.subtotalPrice.amount,
        country: checkout.shippingAddress?.country,
        num_merchant_items_purchased,
        redo_product_id,
      },
    };

    await makePostRequest(
      `${baseRequestUrl}/${widgetId}/treatment-data/completed-checkout`,
      payload,
    );

    await Promise.all(
      splitBasicEvents.map((event) => storage.removeItem(`redo_${event}`)),
    );
  }

  const splitEventCallbacks = new Map() as StorefrontTrackingEventCallbackList;

  // If base_request_url is set, we know that the split settings are enabled because this is an old version of the extension
  for (const event of splitBasicEvents) {
    splitEventCallbacks.set(event as StorefrontTrackingEventType, [
      splitBasicEventCallback as StorefrontTrackingEventCallbackGeneric,
    ]);
  }
  splitEventCallbacks.set("checkout_completed", [
    splitCheckoutCompletedCallback as StorefrontTrackingEventCallbackGeneric,
  ]);

  return splitEventCallbacks;
}
